import {GeometryType} from '../enums/geometry-type.enum';
import {BasePoint} from './base-point';
import {Polygon} from './polygon';
import {GeometryConverter} from '../utils/geometry-converter';
import {SphericalUtil} from '../utils/spherical-util';
import {PolyUtil} from '../utils/poly-util';

/**
 * Which way each outline runs, keyed by the path array a shape rebuilds on
 * every update(). Every corner's angle depends on it, and working it out is a
 * pass over the whole outline: once per corner made a points table of a large
 * parcel quadratic.
 */
const clockwiseByPath = new WeakMap<object, boolean>();

export class PolygonPoint extends BasePoint {

  firstAnimation = false;
  index: number;

  constructor(
    id: string,
    public parent: Polygon,
    latLng: google.maps.LatLngLiteral
  ) {
    super(id, GeometryType.TYPE_POINT);
    this.latLng = latLng;
  }

  /**
   * The point before this one in the stored list, wrapping round at the start.
   *
   * List order, not the outline: it is saved as prev_point_id (read by the
   * mobile apps and the public API) and the panel's arrows step through it.
   * Anything measured uses prevCorner.
   */
  get prev(): PolygonPoint {
    const parent = this.parent;
    if (parent) {
      const index = this.position();
      return parent.points[index === 0 ? parent.points.length - 1 : index - 1];
    }
    return null;
  }

  /** The point after this one in the stored list, wrapping round at the end. See prev. */
  get next(): PolygonPoint {
    const parent = this.parent;
    if (parent) {
      const index = this.position();
      return parent.points[index === parent.points.length - 1 ? 0 : index + 1];
    }
    return null;
  }

  /**
   * The corner before this one along the outline, or null where there is none.
   *
   * prev/next wrap round the list, which measured two things wrongly. A closed
   * shape stores its first corner twice, so that corner's prev was its own
   * closing copy, standing on it; and a line has no side joining its ends, so
   * its end corners were measured against the far end of the line. Here the
   * closing copy is the first corner again (between the last real corner and
   * the second), an area closed without the copy wraps round, and a line's
   * ends have nothing beyond them.
   */
  get prevCorner(): PolygonPoint | null {
    return this.corner(-1);
  }

  /** The corner after this one along the outline, or null where there is none. See prevCorner. */
  get nextCorner(): PolygonPoint | null {
    return this.corner(1);
  }

  private corner(step: number): PolygonPoint | null {
    const parent = this.parent;
    if (!parent) {
      return null;
    }
    const points = parent.points;
    const count = points.length;
    const index = this.position();
    if (index < 0 || count < 2) {
      return null;
    }
    if (this.isClosedRing()) {
      // The corners are every point but the closing copy, which is corner 0.
      const corners = count - 1;
      const at = index === count - 1 ? 0 : index;
      return points[(at + step + corners) % corners];
    }
    const to = index + step;
    if (to >= 0 && to < count) {
      return points[to];
    }
    // Locked or not, as the perimeter and the server's export count it
    // (Polygon.measuresAsClosed), so a points table adds up to the perimeter.
    return parent.measuresAsClosed() ? points[(to + count) % count] : null;
  }

  /** This point's place in its shape's list; `index` is trusted while it is current. */
  private position(): number {
    const points = this.parent.points;
    return points[this.index] === this ? this.index : points.indexOf(this);
  }

  /** Whether the shape ends on a copy of its first point (see Polygon.isClosed). */
  private isClosedRing(): boolean {
    return this.parent.points.length > 2 && this.parent.isClosed();
  }

  /** Whether this point is the copy of the first corner that closes the shape. */
  private isClosingPoint(): boolean {
    return this.isClosedRing() && this.position() === this.parent.points.length - 1;
  }

  getParentId(): string {
    return this.parent.id;
  }

  getIndex() {
    if(this.index > -1){
      return this.index;
    }
    return this.parent.points.indexOf(this);
  }

  update() {
    this.updateLabel();
  }

  toFirebaseData(deep?: boolean): any {
    return GeometryConverter.pointToData(this);
  }

  load(data: any) {
    GeometryConverter.loadDataToPoint(this, data);
  }

  /** Whether this point is a corner with an angle: not an end of a line, nor in a shape of fewer than three points. */
  hasAngle(): boolean {
    return !!(this.prevCorner && this.nextCorner);
  }

  /** The angle inside the shape at this corner, in degrees; 0 where there is none (see hasAngle). */
  getAngle(): number {
    const prev = this.prevCorner;
    const next = this.nextCorner;
    if (prev && next) {
      const angle = SphericalUtil.computeAngle(prev.latLng, this.latLng, next.latLng);
      return this.isClockwise() ? angle : 360 - angle;
    }
    return 0;
  }

  /**
   * Turns the side leaving this corner so that the angle here becomes `angle`
   * (as getAngle reports it), keeping that side's length: the next corner moves.
   */
  setAngle(angle: number) {
    const prev = this.prevCorner;
    const next = this.nextCorner;
    if (prev && next) {
      // getAngle reports 360 minus the raw angle on an anticlockwise outline;
      // without the same flip here the side swung across to the far side of
      // the corner, turning the shape inside out.
      const raw = this.isClockwise() ? angle : 360 - angle;
      const h1 = SphericalUtil.computeHeading360(this.latLng, prev.latLng);
      const toHeading = h1 - raw;
      const distance = SphericalUtil.computeDistanceBetween(this.latLng, next.latLng);
      const newPos = SphericalUtil.computeOffset(this.latLng, distance, toHeading);
      if (newPos) {
        this.moveCorner(next, newPos);
      }

      // const remain = 180 - angle;
      // const a = SphericalUtil.computeDistanceBetween(this.prev.latLng, this.next.latLng);
      // const B = 360 - SphericalUtil.computeAngle(this.latLng, this.prev.latLng, this.next.latLng);
      // const C = 360 - SphericalUtil.computeAngle(this.prev.latLng, this.next.latLng, this.latLng);
      // const prop = C / (C + B);
      // const newC = prop * remain;
      // const newB = remain - newC;
      // const baseHeading = SphericalUtil.computeHeading360(this.prev.latLng, this.next.latLng);
      // const h = Math.abs(baseHeading - newB);
      // // let c = SphericalUtil.computeDistanceBetween(this.prev.latLng, this.next.latLng);
      // const d = Math.abs(Math.sin(SphericalUtil.toRadians(newC)) * (a / Math.sin(SphericalUtil.toRadians(angle))));
      //
      // Logger.log('ANGLE ', B, C, newB, newC, baseHeading, h);
      //
      //
      // const newPos = SphericalUtil.computeOffset(this.prev.latLng, d, h);
      // let A = SphericalUtil.computeAngle(this.next.latLng, this.prev.latLng, this.latLng);
      // let B = 180.0 - A - angle;
      // let azim = SphericalUtil.computeHeading360(this.prev.latLng, this.latLng);
      // let newPos = SphericalUtil.computeOffset(this.prev.latLng, b, azim);

    }
  }

  /** Bearing of the side arriving at this corner, from the corner before; 0 where there is none. */
  getAzimuth(): number {
    const prev = this.prevCorner;
    if (prev) {
      return SphericalUtil.computeHeading360(prev.latLng, this.latLng);
    }
    return 0;
  }

  /** Turns the side arriving at this corner to `azimuth`, keeping its length: this corner moves. */
  setAzimuth(azimuth: number) {
    const prev = this.prevCorner;
    if (prev) {
      const newPos = SphericalUtil.computeOffset(prev.latLng, this.getDistanceToPrevious(), azimuth);
      if (newPos) {
        this.moveCorner(this, newPos);
      }
    }
  }

  /** Length of the side arriving at this corner; undefined where none does. */
  getDistanceToPrevious() {
    const prev = this.prevCorner;
    if (prev) {
      return SphericalUtil.computeDistanceBetween(prev.latLng, this.latLng);
    }
  }

  /**
   * Length of the side leaving this point; undefined where none does: the
   * last point of a line, and a closed shape's closing point, which ends the
   * side the first corner's row already counts. A points table then adds up
   * to the perimeter.
   */
  getDistanceToNext() {
    const next = this.nextCorner;
    if (next && !this.isClosingPoint()) {
      return SphericalUtil.computeDistanceBetween(this.latLng, next.latLng);
    }
  }

  /** See clockwiseByPath. */
  private isClockwise(): boolean {
    const parent = this.parent;
    const path = parent.path;
    // The path is only a faithful key while it is the points' own (not a
    // circle's generated outline, nor one from before points were added).
    if (!path || path.length !== parent.points.length) {
      return PolyUtil.isClockwise(parent.points);
    }
    let clockwise = clockwiseByPath.get(path);
    if (clockwise === undefined) {
      clockwise = PolyUtil.isClockwise(parent.points);
      clockwiseByPath.set(path, clockwise);
    }
    return clockwise;
  }

  /**
   * Moves a corner of this shape. A closed shape's first corner and its
   * closing copy are one corner, so whichever of the two moves, the other goes
   * with it, heights included: moving only one left a fifth corner where the
   * first had stood.
   */
  private moveCorner(point: PolygonPoint, position: google.maps.LatLngLiteral) {
    const parent = this.parent;
    const points = parent.points;
    // Before the move: afterwards the two ends no longer match.
    const closed = this.isClosedRing();
    parent.movePoint(point, position);
    if (!closed) {
      return;
    }
    const first = points[0];
    const closing = points[points.length - 1];
    const twin = point === first ? closing : point === closing ? first : null;
    if (twin && (!twin.equalsPosition(point)
      || twin.altitude !== point.altitude || twin.altitude3d !== point.altitude3d)) {
      twin.move(point.latLng);
      twin.altitude = point.altitude;
      twin.altitude3d = point.altitude3d;
      parent.update();
    }
  }

}
